const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private proactiveTimer: ReturnType<typeof setTimeout> | null = null;
  // #111: callbacks disparados quando o access token e rotacionado — tanto pelo
  // refresh (proativo/reativo) NESTA aba quanto via `storage` de OUTRA aba. Os
  // hooks de socket usam pra atualizar `socket.auth` do socket VIVO sem
  // reconectar, evitando reconexao organica com token congelado no mount.
  private tokenRefreshListeners = new Set<(token: string) => void>();

  constructor() {
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('accessToken');
      this.refreshToken = localStorage.getItem('refreshToken');

      // Outras abas que renovam tokens disparam o evento `storage`.
      // Sem isso, a aba B continuaria com o refresh token antigo em memoria
      // mesmo apos a aba A te-lo rotacionado, causando logout silencioso
      // no proximo refresh da aba B.
      window.addEventListener('storage', (e) => {
        if (e.key === 'accessToken') {
          this.accessToken = e.newValue;
          // #111: outra aba rotacionou o token — propaga aos sockets vivos desta.
          if (e.newValue) this.emitTokenRefreshed(e.newValue);
        }
        if (e.key === 'refreshToken') this.refreshToken = e.newValue;
        if (e.key === null) {
          // localStorage.clear() chamado em outra aba — limpa estado local
          this.accessToken = null;
          this.refreshToken = null;
        }
      });

      // Arma o timer proativo após reload: o constructor já leu o token do
      // localStorage, mas ninguém chamou setTokens → sem isso o refresh só
      // seria agendado na próxima renovação, deixando a janela de 0→80% da
      // vida do token sem proteção após um hard reload.
      if (this.accessToken) {
        this.scheduleProactiveRefresh(this.accessToken);
      }
    }
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    this.scheduleProactiveRefresh(accessToken);
  }

  clearTokens() {
    if (this.proactiveTimer) { clearTimeout(this.proactiveTimer); this.proactiveTimer = null; }
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('gk_permissions');
    localStorage.removeItem('gk_is_admin');
    // #331: a flag de voz e permissao como as outras e sai junto. Enquanto o
    // webphone vivia no layout do dashboard, deslogar o desmontava de tabela;
    // desde que ele subiu pro layout raiz (`VoiceShell`), ele sobrevive ao
    // `router.push('/login')` — sem isto a bolha do ramal fica por cima da tela
    // de login, com permissao stale, ate alguem recarregar.
    localStorage.removeItem('gk_voice_enabled');
    // #422: evento PROPRIO de fim de sessao. Antes disparava
    // `gk:permissions-updated`, e o `useRequireAccess` lia isso como revogacao
    // e mandava pra `/dashboard` antes do `/login` do logout.
    window.dispatchEvent(new Event('gk:auth-cleared'));
  }

  getUser(): AuthUser | null {
    if (typeof window === 'undefined') return null;
    const data = localStorage.getItem('user');
    return data ? JSON.parse(data) : null;
  }

  setUser(user: AuthUser) {
    localStorage.setItem('user', JSON.stringify(user));
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  /**
   * #111: registra um callback chamado SEMPRE que o access token e renovado
   * (refresh proativo/reativo nesta aba ou `storage` de outra aba). Os hooks de
   * socket usam pra trocar `socket.auth` do socket VIVO, pra que qualquer
   * reconexao organica posterior ja parta de token fresco — elimina o 2o
   * handshake + o POST /auth/refresh redundante. Retorna unsubscribe.
   */
  onTokenRefreshed(cb: (token: string) => void): () => void {
    this.tokenRefreshListeners.add(cb);
    return () => {
      this.tokenRefreshListeners.delete(cb);
    };
  }

  private emitTokenRefreshed(token: string) {
    for (const cb of this.tokenRefreshListeners) {
      try {
        cb(token);
      } catch (e) {
        // um listener com bug nao pode derrubar o fluxo de refresh — mas nao
        // engole em silencio (#111 hardening): loga pra um listener quebrado
        // virar um sinal observavel em vez de sumir.
        console.warn('[api] token refresh listener falhou', e);
      }
    }
  }

  /**
   * Forca um refresh do access token via `/auth/refresh`. Resolve `true` se
   * conseguiu renovar (novo accessToken em memoria + localStorage), `false` se
   * falhou (refresh token tambem expirou — clearTokens ja foi chamado em
   * `doRefresh` e redirect pra /login disparado).
   *
   * Usado pelo socket do chat: o `tryRefresh` privado so e acionado em 401 de
   * requisicao HTTP; quando o WS handshake falha com token expirado o socket
   * precisa chamar isso explicitamente antes de tentar reconectar com o novo
   * token. Dedupe via `refreshPromise` interno evita 2 refreshes concorrentes
   * quando uma chamada HTTP e o socket detectam expiracao ao mesmo tempo.
   */
  async refreshTokens(): Promise<boolean> {
    if (!this.refreshToken) return false;
    return this.tryRefresh();
  }

  async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const { skipAuth, ...fetchOptions } = options;

    const isFormData = fetchOptions.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(fetchOptions.headers as Record<string, string>),
    };

    if (!skipAuth && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    // If 401, try to refresh token
    if (response.status === 401 && !skipAuth && this.refreshToken) {
      await this.tryRefresh();

      if (this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        response = await fetch(`${API_URL}${endpoint}`, {
          ...fetchOptions,
          headers,
        });
      }
    }

    if (response.status === 204) {
      return undefined as T;
    }

    // NestJS serializa `return null` de um handler como body vazio (sem "null"
    // como string). response.json() num body vazio lanca
    // "Unexpected end of JSON input". Leer como texto primeiro e so parse se
    // houver conteudo protege contra isso (ex: GET /erp/config quando tenant
    // nao tem config salva ainda — retorna null -> body "" -> antes crashava).
    const rawText = await response.text();
    let data: any = null;
    if (rawText) {
      try {
        data = JSON.parse(rawText);
      } catch {
        // Response nao era JSON. Mantem data=null e deixa os checks de status
        // code abaixo decidirem se joga erro ou retorna null/undefined.
        data = null;
      }
    }

    if (!response.ok) {
      // Intercept 403 PASSWORD_CHANGE_REQUIRED — force redirect to /primeiro-acesso
      if (response.status === 403 && typeof window !== 'undefined') {
        const code =
          typeof data?.message === 'object' && data?.message !== null
            ? (data.message as { code?: string }).code
            : (data?.code as string | undefined);
        if (
          code === 'PASSWORD_CHANGE_REQUIRED' ||
          data?.message === 'PASSWORD_CHANGE_REQUIRED'
        ) {
          if (window.location.pathname !== '/primeiro-acesso') {
            window.location.href = '/primeiro-acesso';
          }
          throw new ApiError('Troca de senha obrigatoria', response.status, data);
        }
      }

      // 429 tem duas origens: o ThrottlerException da propria Platform (#75),
      // que nao manda corpo util — e ai "muitas tentativas" descreve mesmo o
      // que o usuario fez; e o throttle de conta do motor, que manda a causa
      // pronta em PT ("Limite de envios do canal atingido"). Nesse segundo caso
      // o agente enviou UM anexo e quem estourou foi a operacao inteira, entao
      // a generica mente. Preferir sempre a mensagem do backend quando existir.
      if (response.status === 429) {
        throw new ApiError(
          data?.message || 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.',
          429,
          data,
        );
      }

      throw new ApiError(data?.message || 'Erro na requisicao', response.status, data);
    }

    return data as T;
  }

  async requestBlob(endpoint: string, options: ApiRequestOptions = {}): Promise<Blob> {
    const { skipAuth, ...fetchOptions } = options;
    const headers: Record<string, string> = {
      ...(fetchOptions.headers as Record<string, string>),
    };
    if (!skipAuth && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    if (response.status === 401 && !skipAuth && this.refreshToken) {
      await this.tryRefresh();
      if (this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        const retryResponse = await fetch(`${API_URL}${endpoint}`, {
          ...fetchOptions,
          headers,
        });
        if (!retryResponse.ok) {
          const text = await retryResponse.text().catch(() => '');
          throw new ApiError(text || retryResponse.statusText, retryResponse.status);
        }
        return retryResponse.blob();
      }
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new ApiError(text || response.statusText, response.status);
    }
    return response.blob();
  }

  /** Lê o `exp` (segundos) do access token sem verificar assinatura (front). */
  private getTokenExpMs(token: string): number | null {
    try {
      const [, payload] = token.split('.');
      const { exp } = JSON.parse(atob(payload));
      return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /** Agenda refresh a ~80% da vida do token (mata o flicker antes do 401). */
  private scheduleProactiveRefresh(accessToken: string) {
    if (this.proactiveTimer) clearTimeout(this.proactiveTimer);
    this.proactiveTimer = null;
    if (typeof window === 'undefined') return;
    const expMs = this.getTokenExpMs(accessToken);
    if (expMs === null) return;
    const lifetime = expMs - Date.now();
    if (lifetime <= 0) return;
    const fireIn = Math.max(0, Math.floor(lifetime * 0.8));
    this.proactiveTimer = setTimeout(() => { void this.refreshTokens(); }, fireIn);
  }

  private async tryRefresh(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    const run = () => this.doRefresh();
    // Web Locks coordena entre ABAS do mesmo browser: uma só aba executa o
    // refresh por vez; as demais reusam o token rotacionado (sincronizado pelo
    // listener de `storage`). Fallback intra-aba via refreshPromise quando a API
    // não existe (browsers antigos / SSR).
    const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
    const promise: Promise<boolean> = locks?.request
      ? locks.request('gk-token-refresh', run)
      : run();
    this.refreshPromise = promise;
    try {
      return await promise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /** Resolve `true` só quando o token foi de fato renovado. */
  private async doRefresh(): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      });

      if (!response.ok) {
        this.clearTokens();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return false;
      }

      const data: AuthResponse = await response.json();
      this.setTokens(data.accessToken, data.refreshToken);
      this.setUser(data.user);
      // #111: propaga o token novo aos sockets vivos desta aba (sem reconectar).
      this.emitTokenRefreshed(data.accessToken);
      return true;
    } catch {
      // Erro de REDE (offline, wake-from-sleep): NÃO limpar tokens — o refresh
      // token continua válido no servidor e a próxima tentativa com rede de
      // volta renova normalmente. clearTokens aqui deslogava o usuário só
      // porque a rede piscou no momento do refresh.
      return false;
    }
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string | null;
  role: string;
  roleId?: string;
  isGkAdmin?: boolean;
  tenantId: string;
  tenantName: string;
  /** Prefixo do protocolo de atendimento (white-label). Fallback "GKHUB". */
  protocolPrefix?: string;
  mustChangePassword?: boolean;
  /** #102: id do agente no Chatwoot (ChatAgentSync). O front indexa o payload de
   *  presenca (users[chatwootAgentId]) com isto, sem depender do getStatus.
   *  null quando o agente ainda nao tem vinculo de chat (sync pendente). */
  chatwootAgentId?: number | null;
  /** #107: role do agente no Chatwoot ('administrator' | 'supervisor' | 'agent'...).
   *  ⚠️ #308 Fase 3: NAO e mais fonte de escopo. Decidia se o front consumia o
   *  payload account-wide de monitoring do socket — mas so e reescrito no login,
   *  entao quem trocava de cargo hoje seguia com o escopo de ontem. Quem decide
   *  agora e `_meta.canFilterByTeam` do GET, derivado do RBAC vivo no backend
   *  (`monitoring-consume.ts`). Nao voltar a ler isto pra decidir acesso. */
  chatwootRole?: string | null;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

export const api = new ApiClient();
